import React, { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  FormControlLabel,
  FormGroup,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';

import { BorderedGrid } from '@shared/components/borderedGrid';
import CustomSwitch from '@shared/components/forms/theme-elements/CustomSwitch';

import { CampaignNoiseCancellationForm } from './CampaignNoiseCancellationForm';
import { NoiseGuardForm } from './NoiseGuardForm';
import {
  CampaignNoiseProcessingDraft,
  CampaignNoiseProcessingValidation,
} from '../utils/campaignNoiseProcessing.utils';

type CampaignNoiseProcessingFormProps = {
  value: CampaignNoiseProcessingDraft | null;
  onChange: (value: CampaignNoiseProcessingDraft) => void;
  errors?: CampaignNoiseProcessingValidation;
  disabled?: boolean;
};

export const CampaignNoiseProcessingForm: React.FC<CampaignNoiseProcessingFormProps> = ({
  value,
  onChange,
  errors = { noiseGuard: {}, noiseCancellation: {} },
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [expandedCancellation, setExpandedCancellation] = useState(false);
  const [expandedGuard, setExpandedGuard] = useState(false);

  if (!value) return null;

  const updateCancellationEnabled = (enabled: boolean) => {
    onChange({
      ...value,
      noiseCancellation: {
        ...value.noiseCancellation,
        enabled,
        configured: enabled ? true : value.noiseCancellation.configured,
      },
    });
    setExpandedCancellation(enabled);
  };

  const updateGuardEnabled = (enabled: boolean) => {
    onChange({ ...value, noiseGuard: { ...value.noiseGuard, enabled } });
    setExpandedGuard(enabled);
  };

  const stopAccordionToggle = (event: React.MouseEvent) => event.stopPropagation();

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1, p: 2, pt: 3 }}>
      <BorderedGrid sx={{ p: 0, overflow: 'hidden' }}>
        <Accordion
          disableGutters
          elevation={0}
          expanded={expandedCancellation}
          onChange={(_, expanded) => setExpandedCancellation(expanded)}
          sx={{ '&:before': { display: 'none' } }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="campaign-noise-cancellation-content"
            id="campaign-noise-cancellation-header"
            sx={{ minHeight: 72, px: 2.5, '& .MuiAccordionSummary-content': { mr: 1.5 } }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
              <Typography fontSize="16px" fontWeight={600}>
                {t('noiseProcessing.cancellation.title')}
              </Typography>
              <FormGroup>
                <FormControlLabel
                  sx={{ mr: 0 }}
                  onClick={stopAccordionToggle}
                  control={
                    <CustomSwitch
                      checked={value.noiseCancellation.enabled}
                      disabled={disabled}
                      onClick={stopAccordionToggle}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        updateCancellationEnabled(event.target.checked)
                      }
                    />
                  }
                  label=""
                />
              </FormGroup>
            </Stack>
          </AccordionSummary>
          <AccordionDetails
            id="campaign-noise-cancellation-content"
            sx={{ px: 2.5, pb: 2.5, pt: 0 }}>
            <CampaignNoiseCancellationForm
              value={value.noiseCancellation}
              onChange={(noiseCancellation) => onChange({ ...value, noiseCancellation })}
              errors={errors.noiseCancellation}
              disabled={disabled}
              showEnabledToggle={false}
            />
          </AccordionDetails>
        </Accordion>
      </BorderedGrid>
      <BorderedGrid sx={{ p: 0, overflow: 'hidden' }}>
        <Accordion
          disableGutters
          elevation={0}
          expanded={expandedGuard}
          onChange={(_, expanded) => setExpandedGuard(expanded)}
          sx={{ '&:before': { display: 'none' } }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="campaign-noise-guard-content"
            id="campaign-noise-guard-header"
            sx={{ minHeight: 72, px: 2.5, '& .MuiAccordionSummary-content': { mr: 1.5 } }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
              <Typography fontSize="16px" fontWeight={600}>
                {t('noiseProcessing.guard.title')}
              </Typography>
              <FormGroup>
                <FormControlLabel
                  sx={{ mr: 0 }}
                  onClick={stopAccordionToggle}
                  control={
                    <CustomSwitch
                      checked={value.noiseGuard.enabled}
                      disabled={disabled}
                      onClick={stopAccordionToggle}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        updateGuardEnabled(event.target.checked)
                      }
                    />
                  }
                  label=""
                />
              </FormGroup>
            </Stack>
          </AccordionSummary>
          <AccordionDetails id="campaign-noise-guard-content" sx={{ px: 2.5, pb: 2.5, pt: 0 }}>
            <NoiseGuardForm
              value={value.noiseGuard}
              onChange={(noiseGuard) => onChange({ ...value, noiseGuard })}
              errors={errors.noiseGuard}
              disabled={disabled}
              showEnabledToggle={false}
            />
          </AccordionDetails>
        </Accordion>
      </BorderedGrid>
    </Box>
  );
};
